import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    Warning as WarningIcon,
    Block as SuspendIcon,
    Gavel as BanIcon,
    Close as DismissIcon
} from '@mui/icons-material';

interface UserActionModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (actionType: string, reason: string, duration?: number) => Promise<void>;
    actionType: string;
    userEmail: string;
    reportType: string;
    reportContent?: string;
}

const ACTION_CONFIGS = {
    dismiss: {
        title: 'Dismiss Report',
        description: 'This report will be marked as dismissed and no action will be taken against the user.',
        color: 'default',
        icon: <DismissIcon />,
        requiresDuration: false
    },
    warn: {
        title: 'Issue Warning',
        description: 'Send a warning to the user about their behavior. They will receive a notification.',
        color: 'warning',
        icon: <WarningIcon />,
        requiresDuration: false
    },
    suspend: {
        title: 'Suspend User',
        description: 'Temporarily restrict the user\'s access to the platform. They can appeal this decision.',
        color: 'error',
        icon: <SuspendIcon />,
        requiresDuration: true
    },
    ban: {
        title: 'Ban User',
        description: 'Disable account access and hide the profile. Reports and moderation evidence are retained for review and appeals; this does not permanently delete the user’s data.',
        color: 'error',
        icon: <BanIcon />,
        requiresDuration: false
    }
};

const SUSPENSION_DURATIONS = [
    { value: 1, label: '1 Day' },
    { value: 3, label: '3 Days' },
    { value: 7, label: '1 Week' },
    { value: 14, label: '2 Weeks' },
    { value: 30, label: '1 Month' }
];

const UserActionModal: React.FC<UserActionModalProps> = ({
    open,
    onClose,
    onSubmit,
    actionType,
    userEmail,
    reportType,
    reportContent
}) => {
    const [reason, setReason] = useState('');
    const [duration, setDuration] = useState<number>(7);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmationText, setConfirmationText] = useState('');

    const config = ACTION_CONFIGS[actionType as keyof typeof ACTION_CONFIGS];

    const handleSubmit = async () => {
        if (!reason.trim()) {
            setError('Please provide a reason for this action');
            return;
        }
        if (actionType === 'ban' && confirmationText.trim().toUpperCase() !== 'BAN') {
            setError('Type BAN to confirm this account restriction.');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            await onSubmit(actionType, reason.trim(), config.requiresDuration ? duration : undefined);
            
            // Reset form
            setReason('');
            setDuration(7);
            setConfirmationText('');
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to submit action');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setReason('');
            setDuration(7);
            setConfirmationText('');
            setError(null);
            onClose();
        }
    };

    const getSuggestedReasons = () => {
        switch (actionType) {
            case 'dismiss':
                return [
                    'Insufficient evidence',
                    'Does not violate community guidelines',
                    'Report appears to be false or malicious',
                    'Already addressed through other means'
                ];
            case 'warn':
                return [
                    'Minor community guideline violation',
                    'Inappropriate profile content',
                    'Misleading information in profile',
                    'First offense - educational warning'
                ];
            case 'suspend':
                return [
                    'Repeated community guideline violations',
                    'Inappropriate or offensive content',
                    'Harassment or bullying behavior',
                    'Spam or promotional activity'
                ];
            case 'ban':
                return [
                    'Severe harassment or threats',
                    'Illegal or extremely inappropriate content',
                    'Multiple serious violations after warnings',
                    'Creating fake profiles or catfishing'
                ];
            default:
                return [];
        }
    };

    const formatReportType = (type: string) => {
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    if (!config) {
        return null;
    }

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {config.icon}
                    <Box>
                        <Typography variant="h6" component="div">
                            {config.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Action against: {userEmail}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Alert 
                        severity={config.color === 'error' ? 'warning' : 'info'} 
                        sx={{ mb: 2 }}
                    >
                        {config.description}
                    </Alert>

                    {/* Report Context */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Report Details:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip 
                                size="small" 
                                label={formatReportType(reportType)} 
                                color="primary" 
                                variant="outlined" 
                            />
                        </Box>
                        {reportContent && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                "{reportContent}"
                            </Typography>
                        )}
                    </Box>

                    {/* Duration Selection for Suspensions */}
                    {config.requiresDuration && (
                        <Box sx={{ mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Suspension Duration</InputLabel>
                                <Select
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value as number)}
                                    label="Suspension Duration"
                                >
                                    {SUSPENSION_DURATIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Reason Input */}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Reason for Action"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required
                        placeholder="Provide a detailed explanation for this moderation action..."
                        helperText={`This reason will be logged in the admin actions and may be shared with the user.`}
                        error={!!error}
                    />

                    {actionType === 'ban' && (
                        <TextField
                            fullWidth
                            label="Type BAN to confirm"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            disabled={loading}
                            sx={{ mt: 2 }}
                            helperText="The account will be disabled immediately, while evidence remains available for an appeal."
                        />
                    )}

                    {/* Quick Reason Buttons */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Quick reasons:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {getSuggestedReasons().map((suggestedReason, index) => (
                                <Chip
                                    key={index}
                                    label={suggestedReason}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setReason(suggestedReason)}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    color={config.color === 'error' ? 'error' : 'primary'}
                    disabled={!reason.trim() || loading || (actionType === 'ban' && confirmationText.trim().toUpperCase() !== 'BAN')}
                    startIcon={loading ? <CircularProgress size={16} /> : config.icon}
                >
                    {loading ? 'Processing...' : `Confirm ${config.title}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserActionModal;
